import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Package, Search } from 'lucide-react';

import { PICK_TYPE_LABELS, lookupLotLineItems, type LotLookupQuery } from '@/api/shipedgeReports.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { ShipedgeAccessDenied, ShipedgePageHeader } from './_chrome';
import { useShipedgeAdminGate } from './_gate';

/**
 * Lot Lookup page. Phase 15 Day 7.
 *
 * Search line-items by SKU / lot / expiration window / client.
 * Submit-on-button-click rather than auto-fire — saves the spinner
 * dance and the "I typed S then SK then SKU and got 3 round trips"
 * problem on a 90K-row table.
 */

const PAGE_SIZE = 50;

export function LotLookupPage() {
  const { canAccess } = useShipedgeAdminGate();

  // Live form state vs. committed query — committed is what the
  // useQuery hook depends on, so the user can edit fields freely
  // without firing requests until they click Search.
  const [form, setForm] = useState<LotLookupQuery>({});
  const [committed, setCommitted] = useState<LotLookupQuery | null>(null);
  const [offset, setOffset] = useState(0);

  const query = useQuery({
    queryKey: ['shipedge-lot-lookup', committed, offset],
    queryFn: () => lookupLotLineItems({ ...(committed ?? {}), limit: PAGE_SIZE, offset }),
    enabled: canAccess && committed !== null,
    // Keep the previous page's rows visible while the next page loads —
    // page flips previously replaced the whole card with a spinner.
    placeholderData: (previous) => previous,
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    // Strip empties so the URL stays clean.
    const cleaned: LotLookupQuery = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== undefined && v !== ''),
    );
    setOffset(0);
    setCommitted(cleaned);
  }

  if (!canAccess) return <ShipedgeAccessDenied />;

  const total = query.data?.total ?? 0;
  const rows = query.data?.rows ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <ShipedgePageHeader
        title="Lot Lookup"
        description="Search every shipped SKU by lot number, expiration window, or client. Lot/expiration are only populated when tracked at source — empty values are real, not missing."
      />

      <Card className="p-4">
        <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            label="SKU (exact)"
            name="sku"
            value={form.sku ?? ''}
            onChange={(e) => {
              setForm({ ...form, sku: e.target.value });
            }}
            placeholder="e.g. BR__1210U"
          />
          <Input
            label="Lot # (contains)"
            name="skuLot"
            value={form.skuLot ?? ''}
            onChange={(e) => {
              setForm({ ...form, skuLot: e.target.value });
            }}
            placeholder="e.g. 525217"
            hint="Substring match — finds composite and multi-lot values too."
          />
          <Input
            label="Client (slug)"
            name="client"
            value={form.client ?? ''}
            onChange={(e) => {
              setForm({ ...form, client: e.target.value });
            }}
            placeholder="e.g. biotics-research"
            hint="Slug form (lower-case, dashes). See Customer Profitability for the full list."
          />
          <Input
            label="Expires on or after"
            name="skuExpirationAfter"
            type="date"
            value={form.skuExpirationAfter ?? ''}
            onChange={(e) => {
              setForm({ ...form, skuExpirationAfter: e.target.value });
            }}
          />
          <Input
            label="Expires on or before"
            name="skuExpirationBefore"
            type="date"
            value={form.skuExpirationBefore ?? ''}
            onChange={(e) => {
              setForm({ ...form, skuExpirationBefore: e.target.value });
            }}
          />
          <div className="flex items-end">
            <Button type="submit" isLoading={query.isFetching}>
              <Search className="size-4" />
              Search
            </Button>
          </div>
        </form>
      </Card>

      {committed === null ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Package className="size-10 text-kw-ghost" />
          <p className="text-[15px] font-medium text-kw-shark">Type a query and hit Search</p>
          <p className="text-[13px] text-kw-muted">
            Try `Lot # = 525217` to see Adrian&apos;s handoff §7.7 example land.
          </p>
        </Card>
      ) : query.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : query.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Search failed</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {query.error instanceof Error ? query.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-[15px] font-medium text-kw-shark">No matches</p>
          <p className="mt-1 text-[13px] text-kw-muted">
            Loosen the filters, or check that the lot was tracked at source.
          </p>
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <div className="flex items-baseline justify-between px-4 py-2 text-[12px] text-kw-muted">
            <span>
              {total.toLocaleString()} {total === 1 ? 'match' : 'matches'} — showing {offset + 1}–
              {Math.min(offset + PAGE_SIZE, total)}
            </span>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                disabled={offset === 0 || query.isFetching}
                onClick={() => {
                  setOffset(Math.max(0, offset - PAGE_SIZE));
                }}
              >
                Prev
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={offset + PAGE_SIZE >= total || query.isFetching}
                onClick={() => {
                  setOffset(offset + PAGE_SIZE);
                }}
              >
                Next
              </Button>
            </div>
          </div>
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="border-y border-kw-border bg-kw-bg/40 text-left text-[11px] uppercase tracking-wide text-kw-muted">
                <th className="px-4 py-2">Shipped</th>
                <th className="px-4 py-2">Client</th>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2">Lot</th>
                <th className="px-4 py-2">Expires</th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Carrier</th>
                <th className="px-4 py-2">Pick type</th>
                <th className="px-4 py-2 text-right">Total billed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kw-border">
              {rows.map((row) => (
                <tr key={row.lineItemId} className="text-kw-shark hover:bg-kw-bg/30">
                  <td className="whitespace-nowrap px-4 py-2 text-kw-muted">{row.shippedAt.slice(0, 10)}</td>
                  <td className="px-4 py-2 font-medium">{row.client}</td>
                  <td className="px-4 py-2">
                    <span className="font-mono text-[12px]">{row.sku}</span>
                    <div className="text-[11px] text-kw-muted">{row.skuDescription ?? '—'}</div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.unitQty}</td>
                  <td className="px-4 py-2 font-mono text-[12px]">{row.skuLot ?? '—'}</td>
                  <td className="px-4 py-2 text-kw-muted">
                    {row.skuExpiration === null
                      ? '—'
                      : row.skuExpirationLatest !== null && row.skuExpirationLatest !== row.skuExpiration
                        ? `${row.skuExpiration} – ${row.skuExpirationLatest}`
                        : row.skuExpiration}
                  </td>
                  <td className="px-4 py-2 font-mono text-[11px] text-kw-muted">{row.orderNumber}</td>
                  <td className="px-4 py-2">{row.carrier ?? '—'}</td>
                  <td className="px-4 py-2">
                    {row.pickType === null ? '—' : PICK_TYPE_LABELS[row.pickType]}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {row.totalCost === null ? '—' : `$${row.totalCost}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
